import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Loader2, Mail, MapPin, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

const ContactPage = () => {
    const [pageData, setPageData] = useState({ title: 'Contact', content: '' });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPage = async () => {
            try {
                const res = await api.get('/pages/contact');
                setPageData({
                    title: res.data.title || 'Contact',
                    content: res.data.content || ''
                });
            } catch (error) {
                console.error('Error loading contact page:', error);
            } finally {
                setLoading(false);
            }
        };
        loadPage();
    }, []);

    // Simple markdown-like rendering (same as StaticPage)
    const renderContent = (text) => {
        if (!text) return null;
        
        const processInlineFormatting = (line) => {
            if (!line) return line;
            
            const parts = [];
            let remaining = line;
            let key = 0;
            
            while (remaining.length > 0) {
                const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
                const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
                const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
                const codeMatch = remaining.match(/`([^`]+)`/);
                
                const matches = [
                    linkMatch ? { type: 'link', match: linkMatch, index: remaining.indexOf(linkMatch[0]) } : null,
                    boldMatch ? { type: 'bold', match: boldMatch, index: remaining.indexOf(boldMatch[0]) } : null,
                    italicMatch ? { type: 'italic', match: italicMatch, index: remaining.indexOf(italicMatch[0]) } : null,
                    codeMatch ? { type: 'code', match: codeMatch, index: remaining.indexOf(codeMatch[0]) } : null,
                ].filter(Boolean).sort((a, b) => a.index - b.index);
                
                if (matches.length === 0) {
                    if (remaining) parts.push(remaining);
                    break;
                }
                
                const earliest = matches[0];
                
                if (earliest.index > 0) {
                    parts.push(remaining.slice(0, earliest.index));
                }
                
                if (earliest.type === 'link') {
                    parts.push(
                        <a 
                            key={key++} 
                            href={earliest.match[2]} 
                            className="text-primary hover:underline"
                            target={earliest.match[2].startsWith('http') ? '_blank' : undefined}
                            rel={earliest.match[2].startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                            {earliest.match[1]}
                        </a>
                    );
                } else if (earliest.type === 'bold') {
                    parts.push(<strong key={key++}>{earliest.match[1]}</strong>);
                } else if (earliest.type === 'italic') {
                    parts.push(<em key={key++}>{earliest.match[1]}</em>);
                } else if (earliest.type === 'code') {
                    parts.push(
                        <code key={key++} className="bg-muted px-1.5 py-0.5 rounded text-sm">
                            {earliest.match[1]}
                        </code>
                    );
                }
                
                remaining = remaining.slice(earliest.index + earliest.match[0].length);
            }
            
            return parts.length > 0 ? parts : line;
        };
        
        return text.split('\n').map((line, i) => {
            if (line.startsWith('### ')) {
                return <h3 key={i} className="text-xl font-medium mt-4 mb-2">{processInlineFormatting(line.slice(4))}</h3>;
            }
            if (line.startsWith('## ')) {
                return <h2 key={i} className="text-2xl font-semibold mt-6 mb-3">{processInlineFormatting(line.slice(3))}</h2>;
            }
            if (line.startsWith('# ')) {
                return <h1 key={i} className="text-3xl font-bold mt-8 mb-4">{processInlineFormatting(line.slice(2))}</h1>;
            }
            if (line.trim() === '---' || line.trim() === '***') {
                return <hr key={i} className="my-6 border-border" />;
            }
            if (line.startsWith('- ')) {
                return <li key={i} className="ml-4 mb-1 list-disc">{processInlineFormatting(line.slice(2))}</li>;
            }
            if (line.match(/^\d+\. /)) {
                return <li key={i} className="ml-4 mb-1 list-decimal">{processInlineFormatting(line.replace(/^\d+\. /, ''))}</li>;
            }
            if (line.trim() === '') {
                return <br key={i} />;
            }
            return <p key={i} className="mb-2 text-muted-foreground">{processInlineFormatting(line)}</p>;
        });
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{pageData.title} | Colectarium</title>
            </Helmet>
            <div className="min-h-screen bg-background py-8" data-testid="contact-page">
                <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                    <Card className="rounded-2xl">
                        <CardContent className="p-8">
                            {/* Default contact info if no custom content */}
                            {!pageData.content ? (
                                <div className="space-y-8">
                                    <h1 className="text-3xl font-bold" data-testid="contact-page-title">{pageData.title}</h1>
                                    
                                    <div className="space-y-4">
                                        <h2 className="text-xl font-semibold">Informații de contact</h2>
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <User className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-muted-foreground">Persoană de contact</p>
                                                    <p className="font-medium">Călin Miclăuș</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <Mail className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-muted-foreground">Email</p>
                                                    <a href="mailto:contact@colectarium.ro" className="font-medium hover:text-primary transition-colors">
                                                        contact@colectarium.ro
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <MapPin className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-muted-foreground">Locație</p>
                                                    <p className="font-medium">Arad, România</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                                        <h3 className="font-semibold text-blue-900 mb-2">Ai o întrebare?</h3>
                                        <p className="text-blue-700 text-sm">
                                            Pentru întrebări despre comenzi, livrări sau probleme tehnice, 
                                            te rugăm să folosești funcția "Contactează admin" din meniul contului tău.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="prose prose-lg max-w-none" data-testid="contact-page-content">
                                    {renderContent(pageData.content)}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default ContactPage;
